/**
 * Country dialling codes for WhatsApp call-to-action buttons.
 *
 * WhatsApp wants the country code with no leading "+", and rejects a number
 * submitted without one — so the code is picked from this list rather than
 * typed, which removes the single most common reason a phone button bounces.
 *
 * `iso` is the two-letter code shown in the compact display ("IN +91"), while
 * `name` exists so the dropdown can be searched the way people actually think
 * ("india", "united", "971").
 */
export interface CountryDialCode {
  iso: string;
  dial: string;
  name: string;
}

export const COUNTRY_DIAL_CODES: CountryDialCode[] = [
  { iso: "IN", dial: "91", name: "India" },
  { iso: "US", dial: "1", name: "United States" },
  { iso: "GB", dial: "44", name: "United Kingdom" },
  { iso: "AE", dial: "971", name: "United Arab Emirates" },
  { iso: "SA", dial: "966", name: "Saudi Arabia" },
  { iso: "SG", dial: "65", name: "Singapore" },
  { iso: "AU", dial: "61", name: "Australia" },
  { iso: "CA", dial: "1", name: "Canada" },
  { iso: "NZ", dial: "64", name: "New Zealand" },
  { iso: "MY", dial: "60", name: "Malaysia" },
  { iso: "ID", dial: "62", name: "Indonesia" },
  { iso: "PH", dial: "63", name: "Philippines" },
  { iso: "TH", dial: "66", name: "Thailand" },
  { iso: "VN", dial: "84", name: "Vietnam" },
  { iso: "CN", dial: "86", name: "China" },
  { iso: "HK", dial: "852", name: "Hong Kong" },
  { iso: "JP", dial: "81", name: "Japan" },
  { iso: "KR", dial: "82", name: "South Korea" },
  { iso: "TW", dial: "886", name: "Taiwan" },
  { iso: "BD", dial: "880", name: "Bangladesh" },
  { iso: "PK", dial: "92", name: "Pakistan" },
  { iso: "LK", dial: "94", name: "Sri Lanka" },
  { iso: "NP", dial: "977", name: "Nepal" },
  { iso: "BT", dial: "975", name: "Bhutan" },
  { iso: "MV", dial: "960", name: "Maldives" },
  { iso: "AF", dial: "93", name: "Afghanistan" },
  { iso: "QA", dial: "974", name: "Qatar" },
  { iso: "KW", dial: "965", name: "Kuwait" },
  { iso: "BH", dial: "973", name: "Bahrain" },
  { iso: "OM", dial: "968", name: "Oman" },
  { iso: "JO", dial: "962", name: "Jordan" },
  { iso: "LB", dial: "961", name: "Lebanon" },
  { iso: "IL", dial: "972", name: "Israel" },
  { iso: "TR", dial: "90", name: "Turkey" },
  { iso: "EG", dial: "20", name: "Egypt" },
  { iso: "ZA", dial: "27", name: "South Africa" },
  { iso: "NG", dial: "234", name: "Nigeria" },
  { iso: "KE", dial: "254", name: "Kenya" },
  { iso: "TZ", dial: "255", name: "Tanzania" },
  { iso: "UG", dial: "256", name: "Uganda" },
  { iso: "GH", dial: "233", name: "Ghana" },
  { iso: "MA", dial: "212", name: "Morocco" },
  { iso: "DE", dial: "49", name: "Germany" },
  { iso: "FR", dial: "33", name: "France" },
  { iso: "IT", dial: "39", name: "Italy" },
  { iso: "ES", dial: "34", name: "Spain" },
  { iso: "PT", dial: "351", name: "Portugal" },
  { iso: "NL", dial: "31", name: "Netherlands" },
  { iso: "BE", dial: "32", name: "Belgium" },
  { iso: "CH", dial: "41", name: "Switzerland" },
  { iso: "AT", dial: "43", name: "Austria" },
  { iso: "SE", dial: "46", name: "Sweden" },
  { iso: "NO", dial: "47", name: "Norway" },
  { iso: "DK", dial: "45", name: "Denmark" },
  { iso: "FI", dial: "358", name: "Finland" },
  { iso: "IE", dial: "353", name: "Ireland" },
  { iso: "PL", dial: "48", name: "Poland" },
  { iso: "CZ", dial: "420", name: "Czechia" },
  { iso: "GR", dial: "30", name: "Greece" },
  { iso: "RO", dial: "40", name: "Romania" },
  { iso: "HU", dial: "36", name: "Hungary" },
  { iso: "UA", dial: "380", name: "Ukraine" },
  { iso: "RU", dial: "7", name: "Russia" },
  { iso: "BR", dial: "55", name: "Brazil" },
  { iso: "MX", dial: "52", name: "Mexico" },
  { iso: "AR", dial: "54", name: "Argentina" },
  { iso: "CL", dial: "56", name: "Chile" },
  { iso: "CO", dial: "57", name: "Colombia" },
  { iso: "PE", dial: "51", name: "Peru" },
  { iso: "MU", dial: "230", name: "Mauritius" },
  { iso: "FJ", dial: "679", name: "Fiji" },
];

/** Default for this deployment — most students are in India. */
export const DEFAULT_DIAL_ISO = "IN";

/**
 * Matched on iso, name and dial so "in", "india" and "91" all find India.
 *
 * Sorted so a prefix match outranks a mid-string one — typing "in" should
 * surface India before Argentina, which merely contains the letters.
 */
export function searchCountries(query: string): CountryDialCode[] {
  const q = query.trim().toLowerCase().replace(/^\+/, "");
  if (!q) return COUNTRY_DIAL_CODES;

  const scored = COUNTRY_DIAL_CODES.map((c) => {
    const name = c.name.toLowerCase();
    const iso = c.iso.toLowerCase();
    let score = -1;
    if (iso === q || c.dial === q) score = 0;
    else if (name.startsWith(q)) score = 1;
    else if (c.dial.startsWith(q)) score = 2;
    else if (name.includes(q)) score = 3;
    return { c, score };
  }).filter((s) => s.score >= 0);

  scored.sort((a, b) => a.score - b.score || a.c.name.localeCompare(b.c.name));
  return scored.map((s) => s.c);
}

export function findByIso(iso: string): CountryDialCode | undefined {
  return COUNTRY_DIAL_CODES.find((c) => c.iso === iso);
}
